import { Fragment, useEffect, useRef } from 'react'
import cn from 'classnames'
import { ConfirmedSegment, gapBefore, isPause } from '../../../shared/labs/transcript'
import { KeepTalkingModal } from '../../../shared/components/KeepTalkingModal'

// Clues are stored lowercase (as published in the corpus); capitalise the first letter for display.
const capitaliseFirst = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// The definition-underline colour (amber) — matches the reveal's "Meaning" underline so the
// two read identically. An underline (not a fill) is background-independent, so it looks the
// same on the magenta clue card and the light explanation.
const DEF_UNDERLINE = 'underline decoration-2 underline-offset-[3px] decoration-[#e0a53a]'

// Render the clue, optionally underlining the definition span (reveal only — never during play).
const renderClue = (clue: string, defClue?: string, showDef?: boolean): React.ReactNode => {
  const text = capitaliseFirst(clue)
  if (!showDef || !defClue) return text
  const i = text.toLowerCase().indexOf(defClue.toLowerCase())
  if (i < 0) return text
  return (
    <>
      {text.slice(0, i)}
      <span className={DEF_UNDERLINE}>{text.slice(i, i + defClue.length)}</span>
      {text.slice(i + defClue.length)}
    </>
  )
}

// The persistent cryptic play surface: the clue card and the running transcript. Used for
// the whole post-commit journey too (reflection → result), so the player keeps seeing what
// they said while confirmation messages appear underneath (`children`). `middle` is an
// optional slot between clue and transcript (the energy bar, during play). Mirrors the
// 24-game PuzzleStage — including the "hide until you talk" mechanic: when energy is depleted
// (no talking) the clue fades out behind a keep-talking prompt, so thinking aloud is what
// keeps the puzzle visible.
export const CrypticStage = ({
  clue,
  defClue,
  showDef = false,
  dimmed = false,
  confirmedSegments,
  interimText,
  progressLabel,
  showNudge = false,
  middle,
  children,
}: {
  clue: string
  defClue?: string // definition substring to underline (reveal only)
  showDef?: boolean // underline the definition (true only on the reveal screen)
  dimmed?: boolean
  confirmedSegments: ConfirmedSegment[]
  interimText: string
  progressLabel?: string
  showNudge?: boolean
  middle?: React.ReactNode
  children?: React.ReactNode
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [confirmedSegments, interimText, children])

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-4">
      <div className="w-full max-w-lg">
        {progressLabel && (
          <p className="text-center text-xs font-medium uppercase tracking-wide text-gray-400">{progressLabel}</p>
        )}
        <p className="mt-1 text-center text-gray-700">
          Solve the <span className="font-semibold text-gray-900">cryptic clue</span> — think out loud as you work it through.
        </p>
        <p className="mt-1 text-center text-xs text-gray-400">
          The number in brackets is the answer’s length. Say “commit” when you’ve got it.
        </p>

        <div className="relative mt-6">
          <div className="relative rounded-xl bg-[#820458] px-5 py-6 text-center text-white shadow">
            {/* Clue fades out and a keep-talking prompt fades in when energy is depleted
                (no talking) — the clue is genuinely hidden, not just blurred. */}
            <span
              className={cn(
                'block text-lg font-semibold leading-snug transition-opacity duration-700',
                dimmed ? 'opacity-0' : 'opacity-100'
              )}
            >
              {renderClue(clue, defClue, showDef)}
            </span>
            <span
              className={cn(
                'absolute inset-0 flex items-center justify-center px-5 text-center text-sm font-medium transition-opacity duration-700',
                dimmed ? 'opacity-80' : 'opacity-0'
              )}
            >
              Keep talking to reveal the clue…
            </span>
          </div>

          {/* "Keep talking" nudge sits BELOW the clue card AND clear of the energy bar, matching
              the 24 stage. pointer-events-none keeps the controls underneath usable. */}
          {showNudge && (
            <div className="pointer-events-none absolute inset-x-0 top-full z-40 translate-y-2/3 flex justify-center px-2">
              <KeepTalkingModal />
            </div>
          )}
        </div>

        {middle && <div className="mt-6">{middle}</div>}

        <div className="mt-4 text-xs text-gray-400">What we’re hearing</div>
        <div className="relative mt-1">
          <div ref={scrollRef} className="h-40 overflow-y-auto rounded-lg bg-gray-50 px-3 py-2 text-sm leading-relaxed">
            {confirmedSegments.length === 0 && !interimText && (
              <span className="text-gray-400 italic">Start talking through your idea…</span>
            )}
            {confirmedSegments.map((s, i) => {
              const gap = gapBefore(confirmedSegments, i)
              return (
                <Fragment key={i}>
                  {i > 0 &&
                    (isPause(gap) ? (
                      <span className="mx-1 text-xs text-gray-400 select-none whitespace-nowrap align-middle">
                        {'<'}
                        {(gap / 1000).toFixed(1)}s{'>'}
                      </span>
                    ) : (
                      ' '
                    ))}
                  <span className="text-gray-700">{s.text}</span>
                </Fragment>
              )
            })}
            {interimText && <span className="text-gray-400 italic"> {interimText}</span>}
          </div>
        </div>

        {children && <div className="mt-5 pb-10">{children}</div>}
      </div>
    </div>
  )
}
